import classNames from 'classnames';
import { OrderPlacedActionButtons } from 'components/secondaryMarket/shared/SidePanel/OrderPlacedActionButtons';
import { TradeWay } from 'components/secondaryMarket/shared/SidePanel/TradeWay';
import { useFormattedOrderPlaced } from 'components/secondaryMarket/shared/SidePanel/useFormattedOrderPlaced';
import { PanelFooter } from 'components/secondaryMarket/shared/status/Panel';
import { useEffectOnce } from 'hooks/useEffectOnce';
import { useUserNotification } from 'hooks/useUserNotification';
import { IntlKey } from 'locales';
import { last } from 'lodash-es';
import { FC } from 'react';

import { OrderPlacedHeader } from '../../../shared/SidePanel/OrderPlacedHeader';
import { OrderPlacedDetails } from './OrderPlacedDetails';
import type { OrderPlacedSidePanelProps } from './OrderPlacedSidePanel.connect';

function getCashAmountLabel(orderType: OrderPlacedSidePanelProps['orderType']): IntlKey {
  switch (orderType) {
    case 'Market':
      return 'secondaryMarket.sidePanel.label.dlpMarketOrder';
    case 'Limit':
      return 'secondaryMarket.sidePanel.label.dlpLimitOrder';
    default:
      return null as never;
  }
}

function getTradeSideLabel(tradeWay: OrderPlacedSidePanelProps['tradeWay']): string {
  switch (tradeWay) {
    case 'ClientBuy':
      return 'Ask';
    case 'ClientSell':
      return 'Bid';
    default:
      return null as never;
  }
}

export const OrderPlacedSidePanel: FC<OrderPlacedSidePanelProps> = ({
  onDone,
  hasTradeBlotter,
  orderId,
  className,
  closeTabWhenOrderIsPlaced,
  status,
  isin,
  onViewInTradeBlotter,
  ...details
}) => {
  const { summary, hint, formatMessage } = useFormattedOrderPlaced({
    quantity: details.quantity,
    productIsin: isin,
    tradeWay: details.tradeWay,
    execTime: details.execTime!,
    formattedCashAmountLabel: getCashAmountLabel(details.orderType),
    formattedCashAmountValues: {
      tradeSide: getTradeSideLabel(details.tradeWay),
      limit: details.limit,
    },
  });
  const { info } = useUserNotification();
  useEffectOnce(() => {
    if (!closeTabWhenOrderIsPlaced) {
      return;
    }
    const title = formatMessage({ id: 'secondaryMarket.sidePanel.label.orderPlaced' });

    info({
      title: { content: title, severity: 'info' },
      content: [{ content: summary, severity: 'info' }],
    });
    onDone(true);
  });

  const shortenedSummary = last(summary.split('_')) ?? summary;

  return (
    <div className={classNames('d-flex flex-column', className)}>
      <OrderPlacedHeader summary={shortenedSummary} hint={hint} status={status}>
        <div className="d-flex justify-content-between">
          <TradeWay tradeWay={details.tradeWay} />
        </div>
      </OrderPlacedHeader>
      <PanelFooter
        color={status === 'ORDER_STALE' ? 'warning' : 'info'}
        statusKey="secondaryMarket.sidePanel.label.orderPlaced"
        icon="check_circle"
      />
      <div className="flex-grow-1">
        <OrderPlacedDetails orderId={orderId} status={status} isin={isin} {...details} />
      </div>
      <OrderPlacedActionButtons
        hasOrderBlotter={hasTradeBlotter}
        onDone={onDone}
        orderId={orderId}
        blotterRoute="/trade-blotter"
        onNavigateToBlotter={() => onViewInTradeBlotter(isin)}
      />
    </div>
  );
};
OrderPlacedSidePanel.displayName = 'OrderPlacedSidePanel';
